import {
  IHtmlAnnotations,
  IHtmlHighlight,
  IHtmlHighlightInput,
  PopoverConfiguration,
  PopoverConfigurationOptions,
  PopoverOptionComments,
  PopoverOptionNotes,
} from "../../../typings/annotate/annotation";
import { LayoutSizes } from "../../../typings/annotate/utils";
import AnnotatePopover, {
  PopoverInternal,
} from "../annotate-popover/annotate-popover";
import {
  computeThreadPosition,
  getOrderedCommentsThread,
} from "../comments/utils";
import React, { useEffect, useRef, useState } from "react";
import useWindowSize from "../hooks/window-size";
import { isAndroid, isIOS } from "~/utils/device-check";
import { CommentContext } from "../hooks/comment-context";
import { classNames } from "~/utils";
import { injectHighlight } from "~/components/annotate/highlight-injector";
import { getHighlightFromRange } from "~/components/annotate/highlight-util";
import { applySelectionOutcome } from "./selection-outcome";
import { useLocation } from "react-router";
import { useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";
import { toast } from "@mindstonehq/ui";

interface HTMLAnnotatorProps {
  annotations: IHtmlAnnotations;
  editorContent: string;
  popoverConfiguration: PopoverConfiguration;
  trackMetric: (name: string, data?: { [name: string]: string }) => void;
  articleID: string;
  children?: React.ReactElement;
  heightChange?: (x: number) => void;
}

enum layoutSizes {
  small = 2,
  medium = 3,
  large = 4,
  extra = 5,
}
export function HTMLAnnotator(props: HTMLAnnotatorProps) {
  const {
    editorContent,
    popoverConfiguration,
    trackMetric,
    articleID,
    annotations,
  } = props;

  const contentRef = useRef<HTMLDivElement>(null);
  const container = useRef<HTMLDivElement>(null);
  const [currentLayoutSize, setCurrentLayoutSize] = useState<LayoutSizes>(2);
  const [selectedThread, setSelectedThread] = useState<string>();
  const [_, setHighlightsFromBackend] = useState(annotations.highlights);
  const [androidReferenceElement, setAndroidReferenceElement] = useState<any>();
  const windowSize = useWindowSize();
  const [highlights, setHighlights] = useState<IHtmlHighlight[]>(() => {
    const highlights = [...annotations.highlights];
    return getOrderedCommentsThread(highlights);
  });
  const [showComment, setShowComments] = useState(false);
  const [commentsThreadsPosition, setCommentsThreadsPosition] = useState<{
    [id: string]: number;
  }>({});
  const location = useLocation();

  const [fakeHighlight, setFakeHighlight] = useState<
    IHtmlHighlightInput | IHtmlHighlight | null
  >(null);
  const [generatedHtml, setGeneratedHtml] = useState<React.ReactElement>(() => {
    return injectHighlight(
      editorContent,
      annotations.highlights,
      popoverConfiguration,
      trackMetric,
      articleID,
    );
  });

  useEffect(() => {
    setGeneratedHtml(
      injectHighlight(
        editorContent ?? "",
        highlights.filter(
          (x) => !((isAndroid() || isIOS()) && x.id === "temp"),
        ),
        popoverConfiguration,
        trackMetric,
        articleID,
      ),
    );
  }, [editorContent, highlights]);

  const { t } = useLingui();
  const UNINDEXED_TOAST_ID = "highlight-unindexed-selection";
  // Shared by both selection paths; see selection-outcome.ts for the rules.
  // In-article headings carry data-index like any chunk, so a selection that
  // cannot be anchored is one on an image, a caption or page chrome — the
  // warning is always the right answer, and the native selection is kept.
  const selectionEffects = {
    clearPending: () => {
      setSelectedThread("");
      setShowComments(false);
      setFakeHighlight(null);
      setAndroidReferenceElement(null);
    },
    warn: () =>
      toast.warning(t(msg`Select text inside the article to highlight it.`), {
        id: UNINDEXED_TOAST_ID,
      }),
    dismissWarning: () => toast.dismiss(UNINDEXED_TOAST_ID),
  };

  useEffect(() => {
    if (popoverConfiguration.focusMode) {
      // We don't need to listen to selection
      return;
    }
    const selectionChange = (e: any) => {
      // alert(e.type);
      e.preventDefault();
      e.stopPropagation();
      const selection = document.getSelection();
      if (!selection || selection.isCollapsed) {
        if (selection && selection.rangeCount === 0) {
          // Nothing interesting for us.
          return;
        }
        if (e.type === "touchend") {
          // This one I'm not entirely sure. But on iOS, there was a bug with the touchend event when clicking
          // on a highlight after the first time.
          return;
        }
        const range = selection?.getRangeAt(0);
        if (range) {
          const elementsByClassName =
            document.getElementsByClassName("thread_temp");
          if (
            Array.from(elementsByClassName).some((x) =>
              x.contains(range.startContainer),
            )
          ) {
            // It's a click on the container. Keep it as it is.
            return;
          }
        }
        setSelectedThread("");
        setShowComments(false);
        setFakeHighlight(null);
        setAndroidReferenceElement(null);
        return;
      }
      setSelectedThread(""); // Ensure nothing is selected as a thread.

      // IN FF, when highlighting multiple lines in a transcript, it creates multiple ranges
      const rangeForStart = selection.getRangeAt(0);
      let rangeForEnd = selection.getRangeAt(selection.rangeCount - 1);

      let text = "";
      for (let i = 0; i < selection.rangeCount; i++) {
        text += selection.getRangeAt(i).toString();
      }

      const fakeHighlight = getHighlightFromRange(
        rangeForStart,
        rangeForEnd,
        popoverConfiguration,
        text,
        contentRef.current,
      );

      applySelectionOutcome(fakeHighlight, "desktop", {
        ...selectionEffects,
        emptySelection: () => selection.empty(),
        commit: (highlight) => {
          setFakeHighlight(highlight);
          setSelectedThread("temp");
        },
      });
    };

    if (isAndroid() || isIOS()) {
      // This is to cancel the iOS select menu to show. It reselect everything that was selected once you finish selecting
      // and as it's done programmatically, it kills the menu from showing up.
      function createDisableIosSafariCallout(currentRange?: any) {
        const disableIosSafariCallout = (event: any) => {
          if (isIOS()) {
            document.removeEventListener("touchend", disableIosSafariCallout);
          }
          const s = window.getSelection();

          if ((s?.rangeCount || 0) > 0) {
            const r = s?.getRangeAt(0);

            if (r?.toString() === currentRange?.toString()) {
              return;
            }
            s?.removeAllRanges();

            setTimeout(() => {
              s?.addRange(r!);
            }, 50);
          }
        };
        return disableIosSafariCallout;
      }

      const listener = () => {
        const selection = document.getSelection();
        if (!selection || selection.isCollapsed) {
          if (selection && selection.rangeCount === 0) {
            // Nothing interesting for us.
            return;
          }
          return;
        }
        const range = selection.getRangeAt(0);
        if (
          !contentRef.current?.contains(range.startContainer) ||
          !contentRef.current?.contains(range.endContainer)
        ) {
          // Not in the scope
          setSelectedThread("");
          setShowComments(false);
          setFakeHighlight(null);
          setAndroidReferenceElement(null);
          return;
        }

        const text = selection.toString();
        const fakeHighlight = getHighlightFromRange(
          range,
          range,
          popoverConfiguration,
          text,
          contentRef.current,
        );
        applySelectionOutcome(fakeHighlight, "mobile", {
          ...selectionEffects,
          emptySelection: () => {},
          commit: (highlight) => {
            setAndroidReferenceElement({
              getBoundingClientRect: () => {
                return range.getBoundingClientRect();
              },
            });
            setFakeHighlight(highlight);
            setSelectedThread("temp");
          },
        });
      };
      const contentMenuKiller = (event: any) => {
        event.preventDefault();
        event.stopPropagation();
      };
      document.addEventListener("selectionchange", listener);
      document.addEventListener("contextmenu", contentMenuKiller);

      const touchStart = (ev: Event) => {
        const initialRange =
          (window.getSelection()?.rangeCount || -1) > 0
            ? window.getSelection()?.getRangeAt(0)
            : null;
        document.addEventListener(
          "touchend",
          createDisableIosSafariCallout(initialRange),
        );
      };
      if (isIOS()) {
        document.addEventListener("touchstart", touchStart);
      }
      return () => {
        if (isIOS()) {
          document.removeEventListener("touchstart", touchStart);
        }

        document.removeEventListener("selectionchange", listener);
        document.removeEventListener("contextmenu", contentMenuKiller);
      };
    }

    contentRef.current?.addEventListener("mouseup", selectionChange);
    contentRef.current?.addEventListener("touchend", selectionChange);

    return () => {
      contentRef.current?.removeEventListener("mouseup", selectionChange);
      contentRef.current?.removeEventListener("touchend", selectionChange);
    };
  }, [generatedHtml, contentRef, popoverConfiguration.focusMode]);

  useEffect(() => {
    // The highlights have changed, select the last highlight in the list
    setHighlightsFromBackend((prev) => {
      if (prev.length < annotations.highlights.length) {
        // There is a new highlight, we should select it. This is for mobile apps. But with the highlights comming from
        // the websocket, it might be someone else doing something. In that case, we don't want to
        // select it. So basically, only select if the last highlight is the current user's one. It means they created a
        // comment or something like that.
        if (
          annotations.highlights[annotations.highlights.length - 1].profile
            .id !== props.popoverConfiguration.currentUser.id
        ) {
          return annotations.highlights; // Someone else has made a change
        }
        setSelectedThread(
          annotations.highlights[annotations.highlights.length - 1].id,
        );
      }
      return annotations.highlights;
    });
  }, [annotations.highlights]);

  useEffect(() => {
    if (selectedThread !== "temp" && fakeHighlight) {
      setFakeHighlight(null);
      setAndroidReferenceElement(null);
    }
    if (!selectedThread) {
      setShowComments(false);
    }
  }, [selectedThread]);

  useEffect(() => {
    if (popoverConfiguration.focusMode) {
      setHighlights([]);
      return;
    }
    const highlights = [...annotations.highlights];
    // TODO: Find a better way to handle this. When a fake highlight is present, and a new comment is created
    // the selection doesn't work as expected as it overrides the new highlights with the current selection.
    if (fakeHighlight) {
      highlights.push({
        ...fakeHighlight,
        // eslint-disable-next-line lingui/no-unlocalized-strings -- GraphQL __typename
        __typename: "HTMLHighlight",
        id: "temp",
        comments: [],
        canEdit: true,
        createdAt: String(new Date()),
        profile: {
          id: "temp",
          username: "temp",
          displayName: "temp",
          profilePictureURL: "temp",
        },
      });
    }

    const orderedHighlights = getOrderedCommentsThread(highlights);
    setHighlights(orderedHighlights);
  }, [annotations.highlights, fakeHighlight, popoverConfiguration.focusMode]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (container.current && !container.current.contains(target)) {
        setSelectedThread("");
        setShowComments(false);
        setFakeHighlight(null);
        setAndroidReferenceElement(null);
      }
    };
    container.current?.addEventListener("click", handleClickOutside, true);
    return () => {
      container.current?.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  const recalculateComments = () => {
    const parentContainer = document.getElementById("content_container");
    if (!parentContainer) {
      return;
    }

    const positions = computeThreadPosition(
      highlights,
      parentContainer,
      selectedThread,
    );
    setCommentsThreadsPosition(positions);
  };

  useEffect(() => {
    if (!container.current) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      window.requestAnimationFrame(() => {
        if (!Array.isArray(entries) || !entries.length) {
          return;
        }
        if (!container.current) {
          return;
        }

        if (props.heightChange) {
          props.heightChange(container.current?.clientHeight);
        }

        if (container.current?.clientWidth < 450) {
          setCurrentLayoutSize(LayoutSizes.extraSmall);
          return;
        }
        if (container.current?.clientWidth < 768) {
          setCurrentLayoutSize(LayoutSizes.small);
          return;
        }
        if (container.current?.clientWidth < 1024) {
          setCurrentLayoutSize(LayoutSizes.medium);
          return;
        }
        if (container.current?.clientWidth < 1280) {
          setCurrentLayoutSize(LayoutSizes.large);
          return;
        }
        if (container.current?.clientWidth < 1536) {
          setCurrentLayoutSize(LayoutSizes.extra);
          return;
        } else {
          setCurrentLayoutSize(LayoutSizes.extraExtra);
        }
        // your code
      });
    });
    observer.observe(container.current);
    return () => observer.disconnect();
  }, [windowSize]);

  useEffect(() => {
    recalculateComments();
  }, [
    highlights,
    selectedThread,
    generatedHtml,
    windowSize,
    currentLayoutSize,
    showComment,
    popoverConfiguration.focusMode,
  ]);

  useEffect(() => {
    const idInUrl = location?.hash.replace("#", "");
    const highlightFromUrl = annotations.highlights.find(
      (h) => h.id === idInUrl,
    );
    if (highlightFromUrl && highlightFromUrl.comments?.length) {
      showComments(highlightFromUrl);
    }
  }, []);

  const showComments = (h: IHtmlHighlight) => {
    const option = popoverConfiguration.options.find(
      (x) => x.type === PopoverConfigurationOptions.Comments,
    );
    if (!option) {
      return;
    }
    const opt = option as PopoverOptionComments;
    opt.commentsClicked(h);
  };

  const selectedHighlight = highlights.find((x) => x.id === selectedThread);
  return (
    <div ref={container} className={"w-full"}>
      <CommentContext.Provider
        value={{
          showComments,
          showNotes: (h: IHtmlHighlight) => {
            const option = popoverConfiguration.options.find(
              (x) => x.type === PopoverConfigurationOptions.Notes,
            );
            if (!option) {
              return;
            }
            const opt = option as PopoverOptionNotes;
            opt.noteClicked(h);
          },
          selectThread: (thread) => {
            setSelectedThread(thread);
          },
          contentRef: contentRef.current,
          selectedThread,
        }}
      >
        <div className="w-full flex">
          <div
            ref={contentRef}
            id={"content_container"}
            className={classNames(
              `text-muted-foreground relative w-full transition-all`,
              !popoverConfiguration.textLeftAligned ? "text-justify" : "",
            )}
          >
            <div
              className="prose prose-strong:font-bold
               selection:bg-highlight
              dark:prose-invert break-words [&_pre]:max-w-[calc(100vw-64px)] max-w-none"
              onTouchStart={() => {
                setSelectedThread("");
                setShowComments(false);
                setFakeHighlight(null);
              }}
            >
              {generatedHtml}
            </div>
            {props.children && (
              <div className="flex justify-start mt-8">{props.children}</div>
            )}
          </div>
        </div>
        {selectedHighlight && (
          <AnnotatePopover
            generatedHtml={generatedHtml}
            androidRefElement={androidReferenceElement}
          >
            <PopoverInternal
              popoverConfiguration={popoverConfiguration}
              layoutSize={currentLayoutSize}
              highlight={selectedHighlight}
              closePopover={() => setSelectedThread("")}
              commentClicked={(hId: string) => {
                setSelectedThread(hId);
                setShowComments(true);
              }}
              trackMetric={trackMetric}
              removeFakeHighlight={() => setFakeHighlight(null)}
            />
          </AnnotatePopover>
        )}
      </CommentContext.Provider>
    </div>
  );
}
